import { Box, Dialog, DialogProps } from '@medely/web-components';
import { ICondition } from '@medely/web-components/types';
import React from 'react';
import AdminOnlyQualificationDialog from '../AdminOnlyQualificationDialog';
import MissingConditionFormDialog from './MissingConditionFormDialog';
import MissingSignedDocumentDialog from './MissingSignedDocumentDialog';
import { findSignedDocumentQualification } from './useSignedDocumentQualification';
import AuthorizationDialog from '../customConditionForms/AuthorizationDialog/AuthorizationDialog';
import { isAuthorizationType } from '../utils/credentialsUtils';
import { findI9Qualification } from './useI9Qualification';
import MissingI9Dialog from './MissingI9Dialog';
import {
  buildFormCondition,
  buildPayloadFromConditionForm,
} from 'utils/professionalQualifications';
import useProfessionalQualifications from 'hooks/useProfessionalQualifications';
import FacilityOnboardingModal from '../customConditionForms/FacilityOnboardingModal';
import FacilityOnboarding from '@src/routes/jobs/components/JobDetails/FacilityOnboarding';

type CredentialUploadDialogProps = DialogProps & {
  condition: ICondition;
};

const MissingConditionDialog = ({ open, onClose, condition }: CredentialUploadDialogProps) => {
  const { allProfessionalQualifications, submitProfessionalQualification } =
    useProfessionalQualifications();

  const qualification = findSignedDocumentQualification(condition);
  if (qualification) {
    return (
      <MissingSignedDocumentDialog
        qualificationId={qualification.id}
        open={open}
        onClose={onClose}
      />
    );
  }

  const i9Qualification = findI9Qualification(condition);
  if (i9Qualification) {
    return <MissingI9Dialog qualificationId={i9Qualification.id} open={open} onClose={onClose} />;
  }

  const hasExistingPQInReview = condition.qualifications.some((qual) =>
    allProfessionalQualifications.some(
      (pq) => pq.qualification.id === qual.id && pq.status === 'review',
    ),
  );

  if (isAuthorizationType(condition.qualifications[0]) && !hasExistingPQInReview) {
    return <AuthorizationDialog condition={condition} onClose={onClose} open={open} />;
  }

  if (condition.qualifications[0].admin_view_only) {
    return (
      <AdminOnlyQualificationDialog
        open={open}
        onClose={onClose}
        qualificationName={condition.qualifications[0].long_name}
      />
    );
  }

  if (condition.condition_type === 'facility_onboarding') {
    const existingFacilityOnboarding = allProfessionalQualifications.find(
      (pq) => pq.qualification.id === condition.qualifications[0].id,
    );

    const onSubmitFacilityOnboarding = (condition: ICondition) => {
      const payload = buildPayloadFromConditionForm(condition);
      return submitProfessionalQualification(payload, {
        onSuccess: () => {
          onClose?.();
        },
      });
    };

    return !!existingFacilityOnboarding ? (
      <FacilityOnboardingModal
        open={open}
        onClose={onClose}
        credential={existingFacilityOnboarding}
        source="credential"
      />
    ) : (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" title={'SCA Health'}>
        <Box p={3} pb={2}>
          <FacilityOnboarding
            canAttest={true}
            source="book"
            documents={condition.condition_documents || []}
            onButtonClick={() => onSubmitFacilityOnboarding(condition)}
          />
        </Box>
      </Dialog>
    );
  }

  return (
    <MissingConditionFormDialog
      open={open}
      onClose={onClose}
      condition={buildFormCondition(condition)}
    />
  );
};

export default MissingConditionDialog;
